// LLM generated - Model Claude Opus 4.5
import type { ZodRawShape, ZodTypeAny } from 'zod';
import { z } from 'zod';

import type { FieldDescriptor, FieldParseReturnValue } from '../../types/common';
import type { APIField } from '../../types/main';

import { ModalFieldGroupElement } from './ModalFieldGroup';
import type { APIModalFieldGroup, ModalFieldGroup as ModalFieldGroupType } from './types';

/**
 * Validation schema for ModalFieldGroup.
 * Creates a nested object schema that validates child fields.
 * The schema structure matches the form values structure.
 */
const createValidationSchema = (
  field: ModalFieldGroupType,
  getNestedSchema?: (
    fieldKey: string
  ) => { field: [string, ZodTypeAny] } | { fields: [string, ZodTypeAny][] } | undefined
) => {
  // Build nested schema from child fields
  const nestedSchema: ZodRawShape = {};

  if (getNestedSchema) {
    for (const childFieldKey of field.fields) {
      const result = getNestedSchema(childFieldKey);
      if (result) {
        if ('field' in result) {
          const [key, schema] = result.field;
          nestedSchema[key] = schema;
        } else {
          result.fields.forEach(([key, schema]) => {
            nestedSchema[key] = schema;
          });
        }
      }
    }
  }

  // If we have nested schemas, return an object schema
  // Otherwise return optional any (backwards compatibility)
  if (Object.keys(nestedSchema).length > 0) {
    return z.object(nestedSchema);
  }

  return z.any().optional() as ZodTypeAny;
};

/**
 * Parses the API response for ModalFieldGroup.
 * Extracts child fields and registers them separately in the store.
 *
 * Child fields are parsed with a prefix based on the parent's key to ensure
 * unique field keys in the store (similar to how RadioGroupField handles nested fields).
 */
const parseFieldResponse = ({
  field,
  fieldKey,
  parse,
}: {
  field: APIModalFieldGroup;
  fieldKey: string;
  parse: (field: APIField, fieldKeyPrefix?: string) => FieldParseReturnValue;
}): FieldParseReturnValue => {
  // Parse each child field and collect results
  // Use parent's fieldKey as prefix for child fields to ensure unique keys in the store
  const childResults: FieldParseReturnValue[] = [];
  const childFieldKeys: string[] = [];

  // Child fields need the parent's fieldKey as prefix so they're registered correctly
  // when this ModalFieldGroup is loaded via DynamicFields
  const childKeyPrefix = `${fieldKey}:`;

  for (const childField of field.fields) {
    // Pass the parent's fieldKey as prefix for child field keys
    const childResult = parse(childField, childKeyPrefix);
    if (childResult) {
      childResults.push(childResult);
      // Get the key from the parsed result (which now includes the prefix)
      childFieldKeys.push(childResult[0].key);
    }
  }

  // Create the parsed ModalFieldGroup with field keys instead of full definitions
  const parsedField: ModalFieldGroupType = {
    ...field,
    fields: childFieldKeys,
  };

  return [
    {
      key: fieldKey,
      type: 'ModalFieldGroup' as const,
      data: parsedField,
    },
    childResults,
  ];
};

const Descriptor: FieldDescriptor<APIModalFieldGroup, ModalFieldGroupType> = {
  FieldComponent: ModalFieldGroupElement,
  name: 'ModalFieldGroup',
  createValidationSchema,
  parseFieldResponse,
};

export default Descriptor;
